// src/encounter/lib/template-pin.ts
// PROD-2608 — the template pin QuestionBank stamps on the encounter's QuestionnaireResponse
// (PROD-2537, QuestionBank #159), read on the client so a reopened note renders the template it was
// STARTED on instead of re-picking one from the axes.
//
// The pin is server-owned: QuestionBank stamps it at the note start, carries it verbatim while the
// note stays on that template, and discards any pin a client sends. So this module only READS it.
//
// Templates are edited IN PLACE (the version label does not branch content), so "the pinned
// template" can only be fetched as it stands now. Whether that is still what the note was started
// on is answered by the pin's `content-hash` against the template's CURRENT pin, which QuestionBank
// serves at `GET /api/Questions/{id}/pin` (QuestionBank #162) — computed by the same code that
// stamped the note, so equal content always yields an equal string. The client never hashes JSON
// itself: reproducing QuestionBank's serializer byte for byte in a browser is exactly the kind of
// coupling that would report every note as changed, or none.
import type { QRExtensionWire, QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';

export const TEMPLATE_PIN_URL = 'https://scrips.com/fhir/StructureDefinition/template-pin';

/** What a note's pin says about the template it was started on. */
export interface TemplatePin {
  templateId: string;
  /** The template's version LABEL at the note start, e.g. "v3.0". Absent when the author set none. */
  version?: string;
  /** When the template was last saved, at the note start. */
  templateUpdated?: string;
  /** `sha256:{hex}` of the template as served at the note start. */
  contentHash?: string;
  /** When the pin was taken. */
  pinnedAt?: string;
}

/** `GET /api/Questions/{id}/pin` — the template's pin as it stands NOW. */
export interface CurrentTemplatePin {
  template: string;
  version?: string | null;
  templateUpdated?: string | null;
  contentHash?: string | null;
}

const REFERENCE_PREFIX = 'Questionnaire/';

function sub(pin: QRExtensionWire, url: string): QRExtensionWire | undefined {
  return pin.extension?.find((e) => e?.url === url);
}

/**
 * The pin on a stored QuestionnaireResponse, or null when it has none (a note saved before
 * PROD-2537, or one that never named a template) or the pin names no template that parses.
 */
export function readTemplatePin(qr: QuestionnaireResponseResource | null | undefined): TemplatePin | null {
  const pin = qr?.extension?.find((e) => e?.url === TEMPLATE_PIN_URL);
  if (!pin) return null;
  const reference = sub(pin, 'template')?.valueReference?.reference ?? '';
  if (!reference.startsWith(REFERENCE_PREFIX)) return null;
  const templateId = reference.slice(REFERENCE_PREFIX.length).trim();
  if (!templateId) return null;
  const version = sub(pin, 'version')?.valueString ?? undefined;
  const templateUpdated = sub(pin, 'template-updated')?.valueDateTime ?? undefined;
  const contentHash = sub(pin, 'content-hash')?.valueString ?? undefined;
  const pinnedAt = sub(pin, 'pinned-at')?.valueDateTime ?? undefined;
  return {
    templateId,
    ...(version ? { version } : {}),
    ...(templateUpdated ? { templateUpdated } : {}),
    ...(contentHash ? { contentHash } : {}),
    ...(pinnedAt ? { pinnedAt } : {}),
  };
}

/**
 * Where the form on screen stands relative to the template the note was started on.
 *
 *  - `none`        — the note has no pin (fresh, or saved before PROD-2537): nothing to compare.
 *  - `same`        — the current template's hash equals the pin's: the form IS what the note was
 *                    started on.
 *  - `changed`     — the template was edited in place after the note started; the form is the
 *                    current version, and the clinician must be told.
 *  - `unavailable` — the pinned template can no longer be read (retired, or not readable by this
 *                    tenant); the form is whatever the axes picked instead.
 *  - `unknown`     — the comparison could not be made (the current-pin read failed, or the pin
 *                    carries no hash). Never reported as `same`.
 */
export type TemplateDrift = 'none' | 'same' | 'changed' | 'unavailable' | 'unknown';

/** Compare a note's pin with the template's current pin. */
export function templateDrift(pin: TemplatePin | null, current: CurrentTemplatePin | null | undefined): TemplateDrift {
  if (!pin) return 'none';
  if (!current || !pin.contentHash || !current.contentHash) return 'unknown';
  return current.contentHash === pin.contentHash ? 'same' : 'changed';
}
